import React from "react";
import { dateLong } from "./ui";

/*
 * BAND 1 — the funnel.
 *
 * Four stages left to right with the conversion rate in the divider between
 * each pair. This is the band that fixes the thing Reports v1 could not say:
 * v1 drew leads, intro calls, opportunities and new customers as four separate
 * 12-month bar charts, so the only numbers that mattered — the rates BETWEEN
 * consecutive stages — appeared nowhere on the page.
 *
 * Fixed by construction: no drag handle, no "…" menu, no hidden flag. The
 * tiles in band 2 are a user's config; this is not. It is the page's premise,
 * and a Reports page whose funnel can be removed is a Reports page that can be
 * configured back into v1.
 *
 * THIS COMPONENT DECIDES NOTHING. Every rate arrives from
 * src/reports/metrics.js already carrying whether it may be shown as a
 * percentage (`suppressed`) and whether it has finished ageing (`complete`).
 * The one rule enforced here is the corollary: an incomplete cohort is never
 * painted as if it were final — hence the provisional strip, which is the only
 * deliberate addition to the committed design (the mock has no slot for it
 * because the mock's numbers were never provisional).
 */

const n = (value) => (value == null ? "—" : value.toLocaleString());

/*
 * "Jun 3 – Sep 1, 2026", or "Sep 1, 2025 – Sep 1, 2026" when the window crosses
 * a year. dateShort() alone cannot express a range: it appends the year only
 * when the date is outside the CURRENT year, so a 12-month window rendered
 * "Sep 1, 2025 – Sep 1" — the year landing on the wrong end, and missing from
 * the end that needed it.
 */
function windowRange(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  const end = dateLong(to);
  if (a.getFullYear() === b.getFullYear()) {
    return `${a.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end}`;
  }
  return `${dateLong(from)} – ${end}`;
}

/** The window and the method, in words, above the object. */
function caption(funnel, filters) {
  const w = funnel.windows;
  const parts = [
    // from_date/to_date are the dates the reader chose; `to` is the exclusive
    // midnight after them, which printed a range ending Apr 30 as "May 1".
    windowRange(
      funnel.window.from_date || funnel.window.from,
      funnel.window.to_date || funnel.window.to
    ),
    "cohort-tracked",
    `call ≤${w.lead_to_call}d · opp ≤${w.call_to_opp}d of the call · won ≤${w.opp_to_won}d of the opp`,
  ];
  if (filters?.source) parts.push(`${filters.source} only`);
  if (filters?.stage) parts.push(`${filters.stage} onward`);
  return parts.join(" · ");
}

/*
 * A stage cell. The count is the cohort's count at that stage, and because the
 * four stages are a strict chain the rate in each divider is exactly the count
 * on its right over the count on its left — so a reader can check the page's
 * arithmetic by eye, which is the point of putting them on one row.
 */
function Stage({ id, label, value, sub, active, onSelect }) {
  return (
    <button
      type="button"
      className={`crm-funnel__stage${active ? " is-active" : ""}`}
      onClick={() => onSelect(id)}
      aria-pressed={active}
    >
      <span className="crm-funnel__label">{label}</span>
      <span className="crm-funnel__value crm-num">{n(value)}</span>
      <span className="crm-funnel__sub">{sub}</span>
    </button>
  );
}

/*
 * A divider. Reads the rate object rather than a number, so the three states it
 * can be in are all handled in one place:
 *
 *   a rate          the base cleared min_base
 *   n/a             it did not — and the note carries the counts instead, because
 *                   "2 of 3" is information and "66.7%" off that base is not
 *   provisional     the cohort has not finished ageing; more of it can still
 *                   convert, so the number can only go up
 */
function Divider({ id, rate, active, onSelect }) {
  const provisional = !rate.complete;
  // A button since 2026-10-07: a rate opens in the chart like any other stat,
  // one bar per cohort of entry.
  return (
    <button
      type="button"
      className={`crm-funnel__conv${active ? " is-active" : ""}`}
      onClick={() => onSelect(id)}
      aria-pressed={active}
      title="Chart this rate by the period companies entered"
    >
      <span className="crm-funnel__arrow" aria-hidden="true">
        →
      </span>
      <span className={`crm-funnel__rate${rate.suppressed ? " is-muted" : ""}`}>
        {rate.suppressed ? "n/a" : `${rate.rate.toFixed(1)}%`}
      </span>
      <span className="crm-funnel__note">
        {rate.suppressed
          ? `${n(rate.numerator)} of ${n(rate.denominator)} · base under ${rate.min_base}`
          : `${rate.label} ≤${rate.window_days}d`}
        {/* A marker, not the word. Three stacked "PROVISIONAL" tags outweighed
            the rates they qualified; the footer carries the sentence once. */}
        {provisional && <span className="crm-funnel__star" title="Provisional — this rate has not finished ageing">*</span>}
      </span>
    </button>
  );
}

export default function ReportsFunnel({ funnel, sourceCount, filters, selected, onSelectStage }) {
  const { counts, rates } = funnel;

  // Each sub-line states the DEFINITION rather than decorating the number. On a
  // funnel whose stage definitions are the contested part — is an intro call a
  // 'call' row or a 'meeting' row, is an opportunity a proposal or an agreement
  // — the definition is the most useful thing the cell can carry.
  const stages = [
    {
      id: "leads",
      label: "Leads",
      value: counts.leads,
      sub: sourceCount ? `entered, across ${sourceCount} sources` : "companies that entered",
    },
    { id: "calls", label: "Intro calls", value: counts.calls, sub: "first call or meeting held" },
    { id: "opps", label: "Opportunities", value: counts.opps, sub: "agreement sent" },
    { id: "won", label: "New customers", value: counts.won, sub: "became clients" },
  ];
  const dividers = ["lead_to_call", "call_to_opp", "opp_to_won"];

  return (
    <section className="crm-funnel">
      <div className="crm-funnel__head">
        <span className="crm-funnel__eyebrow">Funnel</span>
        <span className="crm-funnel__caption">{caption(funnel, filters)}</span>
      </div>

      <div className="crm-funnel__card">
        <div className="crm-funnel__row">
          {stages.map((stage, i) => (
            <React.Fragment key={stage.id}>
              <Stage
                {...stage}
                active={selected === stage.id}
                onSelect={onSelectStage}
              />
              {dividers[i] && (
                <Divider
                  id={dividers[i]}
                  rate={rates[dividers[i]]}
                  active={selected === dividers[i]}
                  onSelect={onSelectStage}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/*
         * The rule from the metric layer, made visible: nothing incomplete may
         * read as final. Without this the page would print 43.8% in the same
         * type it prints a settled rate, which is the v1 failure in a new shape.
         */}
        {!funnel.complete && (
          <div className="crm-funnel__foot">
            <span className="crm-funnel__flag">Provisional</span>
            <span>
              <b>*</b> This cohort has not finished ageing. Starred rates can only rise as more of
              it converts — final on {dateLong(funnel.matures_on)}.
            </span>
            <span
              className={`crm-funnel__endtoend crm-num${selected === "lead_to_won" ? " is-active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectStage("lead_to_won")}
              onKeyDown={(e) => { if (e.key === "Enter") onSelectStage("lead_to_won"); }}
              title="Chart lead → won by the period companies entered"
            >
              lead → won{" "}
              {rates.lead_to_won.suppressed
                ? `${n(rates.lead_to_won.numerator)} of ${n(rates.lead_to_won.denominator)}`
                : `${rates.lead_to_won.rate.toFixed(1)}%`}
            </span>
          </div>
        )}
        {funnel.complete && (
          <div className="crm-funnel__foot">
            <span>
              {funnel.coverage.opps_inferred_from_won > 0
                ? `${funnel.coverage.opps_inferred_from_won} opportunit${
                    funnel.coverage.opps_inferred_from_won === 1 ? "y was" : "ies were"
                  } inferred from the win, having no proposal or agreement transition recorded.`
                : "Every stage in this cohort has finished ageing."}
            </span>
            <span
              className={`crm-funnel__endtoend crm-num${selected === "lead_to_won" ? " is-active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectStage("lead_to_won")}
              onKeyDown={(e) => { if (e.key === "Enter") onSelectStage("lead_to_won"); }}
              title="Chart lead → won by the period companies entered"
            >
              lead → won{" "}
              {rates.lead_to_won.suppressed
                ? `${n(rates.lead_to_won.numerator)} of ${n(rates.lead_to_won.denominator)}`
                : `${rates.lead_to_won.rate.toFixed(1)}%`}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
